// Backup configuration, a manual run, and the history.
//
// The history is the point of the page as much as the settings are: a backup
// that has been failing quietly for three weeks is worse than no backup, so
// every run is listed with what it captured or why it failed.

import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { fieldErrors, useAsync, useErrorMessage } from '../lib/hooks';
import { useI18n, useT } from '../lib/i18n';
import { useToast } from '../components/Toast';
import { Icon } from '../components/Icon';
import {
  Checkbox, EmptyState, ErrorAlert, LoadingRow, SelectField, TextAreaField, TextField,
} from '../components/ui';
import { formatDate } from '@shared/format';
import type { BackupSettings, BackupSettingsInput } from '@shared/types';
import type { TranslationKey } from '@shared/i18n/index';

const DAY_KEYS: TranslationKey[] = [
  'backup.sunday', 'backup.monday', 'backup.tuesday', 'backup.wednesday',
  'backup.thursday', 'backup.friday', 'backup.saturday',
];

const formatBytes = (bytes: number | null) => {
  if (bytes === null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

export function Backups() {
  const t = useT();
  const settings = useAsync(() => api.backupSettings(), []);
  const runs = useAsync(() => api.backupRuns(), []);
  const navigate = useNavigate();

  if (settings.loading && !settings.data) return <LoadingRow />;
  if (settings.error) return <ErrorAlert message={settings.error} onRetry={settings.reload} />;
  if (!settings.data) return null;

  return (
    <>
      <div className="page-head">
        <div>
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => navigate('/settings')}>
            <Icon name="arrow-left" size={14} />{t('nav.settings')}
          </button>
          <h1 className="page-head__title" style={{ marginTop: 6 }}>{t('backup.title')}</h1>
          <p className="page-head__subtitle">{t('backup.subtitle')}</p>
        </div>
      </div>

      <BackupForm
        initial={settings.data}
        onSaved={() => { settings.reload(); runs.reload(); }}
        onRan={() => { settings.reload(); runs.reload(); }}
      />

      <History runs={runs} />
    </>
  );
}

function BackupForm({ initial, onSaved, onRan }: {
  initial: BackupSettings;
  onSaved: () => void;
  onRan: () => void;
}) {
  const t = useT();
  const { locale } = useI18n();
  const toast = useToast();
  const toMessage = useErrorMessage();

  const [form, setForm] = useState(initial);
  // Secrets are write-only: blank means "leave what is stored alone".
  const [password, setPassword] = useState('');
  const [privateKey, setPrivateKey] = useState('');
  const [passphrase, setPassphrase] = useState('');

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [running, setRunning] = useState(false);

  useEffect(() => { setForm(initial); }, [initial]);

  const set = <K extends keyof BackupSettings>(key: K, value: BackupSettings[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const payload = (): BackupSettingsInput => ({
    enabled: form.enabled,
    frequency: form.frequency,
    hour: form.hour,
    minute: form.minute,
    dayOfWeek: form.dayOfWeek,
    includeFiles: form.includeFiles,
    localEnabled: form.localEnabled,
    localPath: form.localPath,
    localKeepCount: form.localKeepCount,
    sftpEnabled: form.sftpEnabled,
    sftpKeepCount: form.sftpKeepCount,
    sftpHost: form.sftpHost,
    sftpPort: form.sftpPort,
    sftpUsername: form.sftpUsername,
    sftpPath: form.sftpPath,
    sftpAuth: form.sftpAuth,
    // Only sent when typed, so saving the form never wipes a stored secret.
    ...(password ? { sftpPassword: password } : {}),
    ...(privateKey ? { sftpPrivateKey: privateKey } : {}),
    ...(passphrase ? { sftpPassphrase: passphrase } : {}),
  });

  async function save(event?: FormEvent) {
    event?.preventDefault();
    setSaving(true);
    setErrors({});
    setFormError(undefined);
    try {
      await api.updateBackupSettings(payload());
      setPassword(''); setPrivateKey(''); setPassphrase('');
      toast.success(t('settings.saved'));
      onSaved();
    } catch (err) {
      const fields = fieldErrors(err);
      setErrors(fields);
      if (Object.keys(fields).length === 0) setFormError(toMessage(err));
    } finally {
      setSaving(false);
    }
  }

  async function test() {
    setTesting(true);
    try {
      // Saved first: the server tests what is stored, not what is on screen.
      await api.updateBackupSettings(payload());
      const result = await api.testBackupDestination();
      toast.success(t('backup.testOk', { detail: result.detail }));
      onSaved();
    } catch (err) {
      toast.error(toMessage(err));
    } finally {
      setTesting(false);
    }
  }

  async function runNow() {
    setRunning(true);
    try {
      await api.updateBackupSettings(payload());
      const run = await api.runBackup();
      if (run.status === 'success') {
        toast.success(t('backup.ranOk', {
          filename: run.filename, size: formatBytes(run.sizeBytes) }));
      } else {
        toast.error(t('backup.ranFailed'));
      }
      onRan();
    } catch (err) {
      toast.error(toMessage(err));
    } finally {
      setRunning(false);
    }
  }

  return (
    <form onSubmit={save} noValidate className="stack" style={{ marginBottom: 16 }}>
      {formError && <ErrorAlert message={formError} />}

      <div className="card">
        <div className="card__head">
          <h2 className="card__title">{t('backup.title')}</h2>
          <div className="row">
            <span className="muted">
              {t('backup.lastRun')}: {initial.lastRunAt
                ? formatDate(initial.lastRunAt, locale) : t('backup.never')}
            </span>
            {initial.nextRunAt && (
              <span className="badge badge--accent">
                {t('backup.nextRun')}: {new Date(initial.nextRunAt)
                  .toLocaleString(locale === 'es' ? 'es-ES' : 'en-GB',
                    { dateStyle: 'short', timeStyle: 'short' })}
              </span>
            )}
          </div>
        </div>
        <div className="card__body stack">
          <Checkbox label={t('backup.enabled')} checked={form.enabled}
            onChange={(v) => set('enabled', v)} />
          {!form.enabled && <p className="field__hint">{t('backup.disabledNote')}</p>}

          <div className="form-grid form-grid--2">
            <SelectField label={t('backup.frequency')} value={form.frequency}
              onChange={(e) => set('frequency', e.target.value as BackupSettings['frequency'])}>
              <option value="daily">{t('backup.daily')}</option>
              <option value="weekly">{t('backup.weekly')}</option>
            </SelectField>

            {form.frequency === 'weekly' ? (
              <SelectField label={t('backup.dayOfWeek')} value={form.dayOfWeek}
                onChange={(e) => set('dayOfWeek', Number(e.target.value))}>
                {DAY_KEYS.map((key, index) => (
                  <option key={key} value={index}>{t(key)}</option>
                ))}
              </SelectField>
            ) : <div />}

            <TextField label={t('backup.time')} type="time"
              value={`${String(form.hour).padStart(2, '0')}:${String(form.minute).padStart(2, '0')}`}
              onChange={(e) => {
                const [h, m] = e.target.value.split(':');
                set('hour', Number(h) || 0);
                set('minute', Number(m) || 0);
              }} />

          </div>

          <Checkbox label={t('backup.includeFiles')} checked={form.includeFiles}
            onChange={(v) => set('includeFiles', v)} />
          <p className="field__hint">{t('backup.includeFilesHint')}</p>
        </div>
      </div>

      <div className="card">
        <div className="card__head">
          <h2 className="card__title">{t('backup.destination')}</h2>
          <span className="muted">{t('backup.destinationHint')}</span>
        </div>
        <div className="card__body stack">
          {/* Two independent destinations rather than a choice: one archive is
              built and written to each, so the copies are byte-identical. */}
          <div className="destination">
            <Checkbox label={t('backup.localEnabled')} checked={form.localEnabled}
              onChange={(v) => set('localEnabled', v)} />
            {form.localEnabled && (
              <div className="form-grid form-grid--2" style={{ marginTop: 10 }}>
                <TextField label={t('backup.localPath')} hint={t('backup.localPathHint')}
                  wrapperClassName="span-all"
                  placeholder="/var/backups/aresinvoice"
                  value={form.localPath} error={errors.localPath}
                  onChange={(e) => set('localPath', e.target.value)} />
                <TextField label={t('backup.localKeepCount')} numeric inputMode="numeric"
                  hint={t('backup.keepHint')} value={String(form.localKeepCount)}
                  error={errors.localKeepCount}
                  onChange={(e) => set('localKeepCount', Number(e.target.value) || 1)} />
              </div>
            )}
          </div>

          <div className="destination">
            <Checkbox label={t('backup.sftpEnabled')} checked={form.sftpEnabled}
              onChange={(v) => set('sftpEnabled', v)} />
            {form.sftpEnabled && (
              <div className="form-grid form-grid--2" style={{ marginTop: 10 }}>
                <TextField label={t('backup.sftpHost')} value={form.sftpHost}
                  error={errors.sftpHost} onChange={(e) => set('sftpHost', e.target.value)} />
                <TextField label={t('backup.sftpPort')} numeric inputMode="numeric"
                  value={String(form.sftpPort)} error={errors.sftpPort}
                  onChange={(e) => set('sftpPort', Number(e.target.value) || 22)} />
                <TextField label={t('backup.sftpUsername')} value={form.sftpUsername}
                  error={errors.sftpUsername} onChange={(e) => set('sftpUsername', e.target.value)} />
                <TextField label={t('backup.sftpPath')} placeholder="/home/backup/aresinvoice"
                  value={form.sftpPath} error={errors.sftpPath}
                  onChange={(e) => set('sftpPath', e.target.value)} />
                <TextField label={t('backup.sftpKeepCount')} numeric inputMode="numeric"
                  hint={t('backup.keepHint')} value={String(form.sftpKeepCount)}
                  error={errors.sftpKeepCount}
                  onChange={(e) => set('sftpKeepCount', Number(e.target.value) || 1)} />

                <SelectField label={t('backup.sftpAuth')} value={form.sftpAuth}
                  onChange={(e) => set('sftpAuth', e.target.value as BackupSettings['sftpAuth'])}>
                  <option value="password">{t('backup.sftpAuthPassword')}</option>
                  <option value="key">{t('backup.sftpAuthKey')}</option>
                </SelectField>

                {form.sftpAuth === 'password' ? (
                  <TextField label={t('backup.sftpPassword')} type="password" autoComplete="off"
                    wrapperClassName="span-all"
                    hint={form.hasSftpPassword ? t('backup.secretStored') : undefined}
                    value={password} onChange={(e) => setPassword(e.target.value)} />
                ) : (
                  <>
                    <TextAreaField label={t('backup.sftpPrivateKey')} rows={5}
                      wrapperClassName="span-all"
                      hint={form.hasSftpPrivateKey ? t('backup.secretStored') : t('backup.sftpPrivateKeyHint')}
                      placeholder="-----BEGIN OPENSSH PRIVATE KEY-----"
                      value={privateKey} onChange={(e) => setPrivateKey(e.target.value)} />
                    <TextField label={t('backup.sftpPassphrase')} type="password" optional
                      autoComplete="off" wrapperClassName="span-all"
                      value={passphrase} onChange={(e) => setPassphrase(e.target.value)} />
                  </>
                )}
              </div>
            )}
          </div>

          {!form.localEnabled && !form.sftpEnabled && (
            <div className="alert alert--warning">
              <Icon name="alert-circle" />
              <div>{t('backup.noDestination')}</div>
            </div>
          )}
        </div>
        <div className="card__foot">
          <div className="row">
            <button type="button" className="btn" onClick={() => void test()} disabled={testing || saving}>
              {testing ? <span className="spinner" /> : <Icon name="check-circle" />}
              {testing ? t('backup.testing') : t('backup.test')}
            </button>
            <button type="button" className="btn" onClick={() => void runNow()} disabled={running || saving}>
              {running ? <span className="spinner" /> : <Icon name="save" />}
              {running ? t('backup.running') : t('backup.runNow')}
            </button>
            <a className="btn btn--ghost" href={api.backupDownloadUrl()} title={t('backup.downloadHint')}>
              <Icon name="download" />{t('backup.download')}
            </a>
          </div>
          <button type="submit" className="btn btn--primary" disabled={saving}>
            {saving ? <span className="spinner" /> : <Icon name="save" />}
            {saving ? t('common.saving') : t('common.saveChanges')}
          </button>
        </div>
      </div>

      <div className="alert alert--info">
        <Icon name="alert-circle" />
        <div>
          <strong>{t('backup.restoreTitle')}. </strong>{t('backup.restoreSteps')}
        </div>
      </div>
    </form>
  );
}

function History({ runs }: { runs: ReturnType<typeof useAsync<import('@shared/types').BackupRun[]>> }) {
  const t = useT();
  const { locale } = useI18n();

  const when = (value: string) => new Date(value)
    .toLocaleString(locale === 'es' ? 'es-ES' : 'en-GB', { dateStyle: 'short', timeStyle: 'medium' });

  return (
    <div className="card">
      <div className="card__head"><h2 className="card__title">{t('backup.history')}</h2></div>
      {runs.loading && !runs.data ? <LoadingRow />
        : runs.error ? <div className="card__body"><ErrorAlert message={runs.error} onRetry={runs.reload} /></div>
        : (runs.data ?? []).length === 0 ? (
          <EmptyState icon="clock" title={t('backup.historyEmpty')} hint={t('backup.historyEmptyHint')} />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th className="shrink">{t('backup.started')}</th>
                  <th className="shrink">{t('common.status')}</th>
                  <th className="shrink">{t('backup.destination')}</th>
                  <th>{t('backup.contents')}</th>
                  <th className="num">{t('backup.size')}</th>
                </tr>
              </thead>
              <tbody>
                {runs.data?.map((run) => (
                  <tr key={run.id}>
                    <td className="shrink nowrap">
                      {when(run.startedAt)}
                      <div className="table__meta">
                        {t(run.triggerType === 'manual'
                          ? 'backup.triggerManual' : 'backup.triggerScheduled')}
                      </div>
                    </td>
                    <td className="shrink">
                      <span className={`badge badge--${
                        run.status === 'success' ? 'paid'
                        : run.status === 'failed' ? 'pending' : 'neutral'}`}>
                        {t(run.status === 'success' ? 'backup.statusSuccess'
                          : run.status === 'failed' ? 'backup.statusFailed' : 'backup.statusRunning')}
                      </span>
                    </td>
                    <td className="shrink table__meta">
                      {t(run.destination === 'sftp'
                        ? 'backup.destinationSftp' : 'backup.destinationLocal')}
                    </td>
                    <td>
                      {run.status === 'failed' ? (
                        <span className="field__error">{run.error}</span>
                      ) : (
                        <>
                          <div className="mono truncate">{run.filename || '—'}</div>
                          {run.tableCount !== null && (
                            <div className="table__meta">
                              {t('backup.contentsSummary', {
                                tables: run.tableCount,
                                rows: run.rowCount ?? 0,
                                files: run.fileCount ?? 0,
                              })}
                            </div>
                          )}
                        </>
                      )}
                    </td>
                    <td className="num">{formatBytes(run.sizeBytes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}
